import React, { useCallback, useMemo, useState } from 'react';
import { TableHeaderCell } from 'common/components';
import type { ManagerAgentAnalyticsItem } from 'services/api/getManagerAgentAnalytics';
import {
    compareMetricSortValues,
    getMetricSortValue,
    type PerformanceMetricSortKey,
    type SortDirection,
} from '../components/PerformanceAnalyticsTab.utils';

export const usePerformanceAgentsSort = <T extends ManagerAgentAnalyticsItem>(agents: T[]) => {
    const [sortField, setSortField] = useState<PerformanceMetricSortKey | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const handleSort = useCallback(
        (field: PerformanceMetricSortKey) => {
            if (sortField === field) {
                setSortDirection((prev: SortDirection) => (prev === 'asc' ? 'desc' : 'asc'));
                return;
            }
            setSortField(field);
            setSortDirection('desc');
        },
        [sortField]
    );

    const sortedAgents = useMemo(() => {
        if (!sortField) return agents;

        return [...agents].sort((a, b) =>
            compareMetricSortValues(getMetricSortValue(a, sortField), getMetricSortValue(b, sortField), sortDirection)
        );
    }, [agents, sortField, sortDirection]);

    const metricSortHeader = useCallback(
        (label: string, field: PerformanceMetricSortKey) => (
            <TableHeaderCell
                label={label}
                sort={{
                    isSorted: sortField === field,
                    direction: sortDirection,
                    onSort: () => handleSort(field),
                }}
            />
        ),
        [sortField, sortDirection, handleSort]
    );

    return {
        sortedAgents,
        metricSortHeader,
        sortField,
        sortDirection,
    };
};
